/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useState} from 'react';
import {Box, Grid, Link, Skeleton} from '@mui/material';
import {shallowEqual, useSelector} from 'react-redux';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {Image} from '@jobseeker/lotus/components/Image';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks/common';

import type {AdsElementType} from '@jobseeker/common-core/types';
import type {AppState} from '@jobseeker/common-core/types';
import {
  getCompanyNameFromShowcase,
  getKeyFromPath,
  parseShowcaseUrl,
  TrackEventUBA,
  getUbaPageName,
  openUrl,
} from '@jobseeker/common-core/utils';

const CompanyGallery = () => {
  const [adsContent, setAdsContent] = useState(Array<AdsElementType>);

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory,
    shallowEqual
  )?.toString();

  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const interceptor: any = useInterceptor();
  const {
    page,
    ads: {
      showCase: {slot},
      jobfeedShowCase: {slot: slotJobfeed},
    },
  } = interceptor;

  const trackEvent = () => {
    const emp = adsContent[0];

    TrackEventUBA('companyBannerClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? tagId : undefined,
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
      companyID: emp?.companyId || parseShowcaseUrl(emp?.link) || '',
      companyName: emp?.title || getCompanyNameFromShowcase(emp?.link) || '',
    });
  };

  useEffect(() => {
    if (!adsContent?.length) {
      return;
    }
    const employer = adsContent[0];

    TrackEventUBA('companyBannerView', 'view', {
      ref: getKeyFromPath('ref') || '',
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? tagId : undefined,
      page: getUbaPageName(),
      sessionUsername: sessionStorage.getItem('userId') || '',
      companyID: employer?.companyId || parseShowcaseUrl(employer?.link) || '',
      companyName:
        employer?.title || getCompanyNameFromShowcase(employer?.link) || '',
    });
  }, [adsContent]);

  const regex = /^([a-z0-9-&]+)-jobs(?:-in-([a-z]+))?$/;

  const matchedForCityPage = page?.match(regex);

  useLoadAds(
    page === 'jobfeed' || !!matchedForCityPage ? slotJobfeed : slot,
    setAdsContent,
    page === 'jobfeed' || !!matchedForCityPage
      ? {}
      : {
          categoryIds: parseInt(categoryId ?? '0') > 0 ? categoryId : 5,
        },
    false
  );

  const isAboveMobile = useIsLargeDevice('md');

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: {xxs: 160, md: 200},
        height: {xxs: 160, md: 200},
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {adsContent?.length > 0 ? (
        <>
          {' '}
          <Grid
            container
            sx={{
              minHeight: {xxs: 160, md: 200},
              height: {xxs: 160, md: 240},
              flexWrap: 'nowrap',
              justifyContent: 'center',
            }}
          >
            <a
              onClick={e => {
                e.preventDefault();
                setTimeout(() => {
                  trackEvent();
                  openUrl(adsContent[0]?.link || '/', '_blank');
                }, 0);
              }}
              href={adsContent[0]?.link}
              target="__blank"
              style={{
                textDecoration: 'none',
                display: 'flex',
                width: '100%',
              }}
            >
              {adsContent?.map((adItem, index) => (
                <Grid
                  item
                  key={index}
                  sx={{
                    flexShrink: 0,
                    overflow: 'hidden',
                    width: {xxs: '100%', md: `${100 / adsContent.length}%`},
                    px: '1px',
                  }}
                >
                  <Box
                    sx={{
                      overflow: 'hidden', // clip the image
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Box
                      component={Image}
                      src={adItem?.imageUrl}
                      alt={`Office ${index + 1}`}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        overflow: 'hidden', // crucial to clip the zoomed image
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.05) !important;',
                          transition:
                            '-ms-transform 450ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s, -webkit-transform 450ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s, transform 450ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s !important;',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </a>
          </Grid>
          {/* Overlay Section */}
          <Box
            display={{xxs: 'flex', md: 'grid'}}
            sx={{
              position: {xxs: 'relative', md: 'absolute'},
              bottom: {xxs: 98, md: 22},
              right: {xxs: 22, md: 20},
              textAlign: 'center',
              justifyContent: {xxs: 'space-between', md: 'center'},
              alignItems: 'end',
            }}
          >
            <Image
              src={adsContent[0]?.companyLogo || ''}
              alt={'showcase_company'}
              width={isAboveMobile ? 112 : 80}
              height={isAboveMobile ? 112 : 80}
              style={{
                marginLeft: isAboveMobile ? 20 : 45,
                maxWidth: isAboveMobile ? 112 : 80,
                maxHeight: isAboveMobile ? 112 : 80,
                position: 'relative',
                objectFit: 'contain',
              }}
            />
            <Link
              onClick={e => {
                e.preventDefault();
                setTimeout(() => {
                  trackEvent();
                  openUrl(adsContent[0]?.link || '/', '_blank');
                }, 0);
              }}
              href={adsContent[0]?.link}
              target="_blank"
              sx={{
                color: '#000',
                height: {xxs: '28px', md: 'auto'},
                textTransform: 'none',
                p: {xxs: '6px 13px 11px', md: '11px 13px'},
                borderRadius: 1,
                fontSize: 12,
                mt: {xxs: 0, md: 1.5},
                fontWeight: 500,
                backgroundColor: '#fff',
                boxShadow: '0 4px 11px 0 rgba(0,0,0,.39)',
                transition: 'all .3s',
                textDecoration: 'none',
                ':hover': {
                  boxShadow: 'none',
                },
                cursor: 'pointer',
              }}
            >
              VISIT COMPANY PAGE
            </Link>
          </Box>
        </>
      ) : (
        <Grid
          container
          sx={{
            minHeight: {xxs: 160, md: 220},
            height: {xxs: 120, md: 220},
            flexWrap: 'nowrap',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {[...new Array(isAboveMobile ? 3 : 1)].map((elem, index) => (
            <Skeleton
              key={`skele_${index}`}
              variant="rounded"
              width={'100%'}
              height={'100%'}
              sx={{
                borderRadius: 2,
              }}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CompanyGallery;
